import { FC, useEffect, useRef, useState } from 'react';
import './shareMenuComponent.scss';
import { emailCopy, shareMessages } from './model';
import { RootState } from '../../redux/AppReducer';
import { useSelector } from 'react-redux';
import {
	IToastState,
	ITrackingEvent,
	TrackingEventStatus,
	TrackingEventType
} from '../../common/view-model';
import { handleTracking } from '../../helpers/handelTracking';
import { v4 as uuidv4 } from 'uuid';
import { ToastActions } from '../../redux/toast/ToastActions';
import { useAppDispatch } from '../../redux/store';

interface IShareMenuComponentProps {
	id: string | number | undefined;
	type: 'brand' | 'product';
}

const ShareMenuComponent: FC<IShareMenuComponentProps> = ({ id, type }) => {
	const dispatch = useAppDispatch();
	const [open, setOpen] = useState<boolean>(false);
	const sortButtonRef = useRef<HTMLDivElement>(null);
	const userState = useSelector((state: RootState) => state.appState.userState?.user);
	const emailShareClick = () => {
		window.location.href = `mailto:?body=${
			type === 'brand' ? emailCopy.message_brand : emailCopy.message_product
		} ${getShareLink()}`;
		setOpen(false);
	};
	const getShareLink = () => {
		let link = '';
		if (type === 'brand') {
			link = `${window.location.origin}/brand/${id}`;
		} else {
			link = `${window.location.origin}/detail/${id}`;
		}
		return link;
	};
	const setToast = (toastContent: IToastState) => dispatch(ToastActions.updateToast(toastContent));
	const copyShareLink = () => {
		let link = getShareLink();
		navigator.clipboard.writeText(link);
		setToast({
			title: shareMessages.toast_text,
			message: '',
			type: 'success',
			position: 'top-right',
			closeOnClick: false,
			autoClose: 2000
		});
	};
	const handleShareClick = (eventType: TrackingEventType, action: string) => {
		const trackingPayload: ITrackingEvent = {
			transactionId: uuidv4(),
			entityId: id?.toString()!,
			status: TrackingEventStatus.SUCCESS,
			type: eventType,
			timeStamp: new Date().toISOString(),
			eventPayload: {
				id: id?.toString()!,
				name: action,
				type: eventType,
				url: window.location.href,
				tags: [],
				metadata: {
					user_type: userState?.user?.user_type,
					user_id: userState?.user?.id,
					environment: import.meta.env.VITE_ENV_NAME
				}
			}
		};
		handleTracking(trackingPayload);
	};

	useEffect(() => {
		function handleClickShareButton(e: MouseEvent) {
			if (sortButtonRef.current && !e.composedPath().includes(sortButtonRef.current)) {
				setOpen(false);
			} else {
				setOpen(true);
			}
		}

		window.addEventListener('click', handleClickShareButton);
		return () => window.removeEventListener('click', handleClickShareButton);
	}, []);

	return (
		<div
			className='share-menu-container'
			onClick={e => {
				e.stopPropagation();
				setOpen(opened => !opened);
			}}
		>
			<div
				ref={sortButtonRef}
				className='icon-container'
				onClick={() =>
					handleShareClick(
						type === 'product'
							? TrackingEventType.CLICK_ON_SHARE_ICON_PRODUCT_PAGE
							: TrackingEventType.CLICK_ON_SHARE_ICON_BRAND_PAGE,
						'click-icon'
					)
				}
			>
				<i className='ri-share-2-line'></i>
			</div>
			{open === true && (
				<div className='share-menu-options'>
					<div
						className='menu-item'
						onClick={() => {
							emailShareClick();
							handleShareClick(
								type === 'product'
									? TrackingEventType.CLICK_ON_SHARE_EMAIL_PRODUCT_PAGE
									: TrackingEventType.CLICK_ON_SHARE_EMAIL_BRAND_PAGE,
								'click-email'
							);
						}}
					>
						<i className='icon ri-mail-line'></i>
						<span className='text'>{shareMessages.email_text}</span>
					</div>
					<div
						className='menu-item'
						onClick={() => {
							copyShareLink();
							handleShareClick(
								type === 'product'
									? TrackingEventType.CLICK_ON_SHARE_LINK_PRODUCT_PAGE
									: TrackingEventType.CLICK_ON_SHARE_LINK_BRAND_PAGE,
								'click-link'
							);
						}}
					>
						<i className='icon ri-links-line'></i>
						<span className='text'>{shareMessages.copy_text}</span>
					</div>
				</div>
			)}
		</div>
	);
};

export default ShareMenuComponent;
